import { useCallback, useEffect, useRef, useState } from 'react'
import { failureMessageOf } from './libraryFailure'

/** D16.8: Processes and Technologies are asked 20 at a time. */
export const LIBRARY_PAGE_SIZE = 20
export const SEARCH_WAIT_MS = 300
const NEVER_REQUESTED = Symbol('never requested')

/** The paginated request `getWorkflowTemplates` and `technologiesWorkflow` already take (SmartPaletteSection.jsx:50-65). */
export const libraryPageRequest = ({ page, term, mixedColumnFilters = null }) => ({
  parameterFilters: [],
  parameterFiltersValues: [],
  parameterFiltersNames: [],
  columns: ['id', 'code', 'name'],
  page,
  size: LIBRARY_PAGE_SIZE,
  columnFilters: ['name'],
  columnFilterValues: term ? [term.toLowerCase()] : null,
  sortKey: [],
  sortMode: [],
  rows: [],
  primaryKeys: ['id'],
  allSelected: false,
  mixedColumnFilters
})

/** RN-06.3: what is typed, trimmed (only spaces = no search) and asked for once the user stops typing. */
export const useDebouncedTerm = (text) => {
  const [term, setTerm] = useState(text.trim())
  useEffect(() => {
    const timer = setTimeout(() => setTerm(text.trim()), SEARCH_WAIT_MS)
    return () => clearTimeout(timer)
  }, [text])
  return term
}

const EMPTY_LIST = { items: [], loadedPages: 0, totalElements: 0, loading: false, failed: false, failureMessage: '', pageFailed: false, pageFailureMessage: '' }

/**
 * RN-10.3 (D12): a list that asks the next page when the user reaches its end. A new term starts again from
 * page 1; a failed first page is retried the next time the list becomes active or the term changes (RN-11.3);
 * a failed next page keeps what is loaded and is retried on the next arrival at the end (E-14).
 *
 * @param {{ fetchPage: function(number): Promise<{content: Object[], totalElements: number}>, term: string, active: boolean }} params
 */
export const usePagedLibraryList = ({ fetchPage, term, active }) => {
  const [list, setList] = useState(EMPTY_LIST)
  const listRef = useRef(EMPTY_LIST)
  listRef.current = list
  const requestedTermRef = useRef(NEVER_REQUESTED)
  const generationRef = useRef(0)
  // The caller may hand a new function on every render: reading it from a ref keeps the load stable, so a
  // failed first page is retried only when the list becomes active again or the term changes (RN-11.3).
  const fetchPageRef = useRef(fetchPage)
  fetchPageRef.current = fetchPage

  const loadPage = useCallback(async (page, generation) => {
    setList((current) => ({ ...current, loading: true }))
    try {
      const response = await fetchPageRef.current(page)
      if (!Array.isArray(response?.content)) {
        throw response
      }
      if (generation !== generationRef.current) {
        return
      }
      setList((current) => ({
        ...EMPTY_LIST,
        items: page === 0 ? response.content : [...current.items, ...response.content],
        loadedPages: page + 1,
        totalElements: response.totalElements || 0
      }))
    } catch (thrown) {
      if (generation !== generationRef.current) {
        return
      }
      setList((current) => (page === 0
        ? { ...EMPTY_LIST, failed: true, failureMessage: failureMessageOf(thrown) }
        : { ...current, loading: false, pageFailed: true, pageFailureMessage: failureMessageOf(thrown) }))
    }
  }, [])

  useEffect(() => {
    if (!active) {
      return
    }
    if (requestedTermRef.current === term && !listRef.current.failed) {
      return
    }
    requestedTermRef.current = term
    generationRef.current += 1
    setList(EMPTY_LIST)
    loadPage(0, generationRef.current)
  }, [active, term, loadPage])

  const loadNext = useCallback(() => {
    const current = listRef.current
    if (current.loading || current.failed || current.items.length >= current.totalElements) {
      return
    }
    loadPage(current.loadedPages, generationRef.current)
  }, [loadPage])

  return { ...list, loadNext }
}
